import isTaxID from 'validator/lib/isTaxID';

export enum CountryCodeEnum {
  BG = 'BG',
  CH = 'CH',
  IS = 'IS',
  LI = 'LI',
  NO = 'NO',
  TR = 'TR',
  DE = 'DE',
  RO = 'RO',
  LT = 'LT',
  IT = 'IT',
  CY = 'CY',
  PL = 'PL',
}

const countryCodeToValidatorLocaleMap: Record<CountryCodeEnum, string> = {
  [CountryCodeEnum.BG]: 'bg-BG',
  [CountryCodeEnum.CH]: 'de-CH',
  [CountryCodeEnum.IS]: 'is-IS',
  [CountryCodeEnum.LI]: 'de-LI',
  [CountryCodeEnum.NO]: 'nb-NO',
  [CountryCodeEnum.TR]: 'tr-TR',
  [CountryCodeEnum.DE]: 'de-DE',
  [CountryCodeEnum.RO]: 'ro-RO',
  [CountryCodeEnum.LT]: 'lt-LT',
  [CountryCodeEnum.IT]: 'it-IT',
  [CountryCodeEnum.CY]: 'el-CY',
  [CountryCodeEnum.PL]: 'pl-PL',
};

function getValidatorLocaleFromCountryCode(
  countryCode: CountryCodeEnum
): string {
  return countryCodeToValidatorLocaleMap[countryCode] || countryCode;
}

// These countries are not supported by the validator library
const unsupportedCountries = [
  CountryCodeEnum.BG, // validator library does not validate some IDs correctly
  CountryCodeEnum.CH,
  CountryCodeEnum.IS,
  CountryCodeEnum.LI,
  CountryCodeEnum.NO,
  CountryCodeEnum.TR,
  CountryCodeEnum.DE, // validator library does not validate accordingly, wrong regex
  CountryCodeEnum.RO, // Validations does not pass for some IDs eg. 2930126022988 or 1780529284377. It works on development but not in production
  CountryCodeEnum.LT,
];

// For these countries, we should check the existing regex if the library fails
const countriesToDoubleCheck = [
  CountryCodeEnum.IT,
  CountryCodeEnum.CY,
  CountryCodeEnum.PL,
];

const taxIdRegex = {
  [CountryCodeEnum.BG]: /^\d{10}$/, // eg. 10 digits
  [CountryCodeEnum.CH]: /^CHE-?(\d{3}\.\d{3}\.\d{3}|\d{9}) ?(MWST|TVA|IVA)$/i, // eg. "CHE-123.456.789 MWST", "CHE123456789MWST" — suffix required, hyphen and dots optional, case-insensitive
  [CountryCodeEnum.IS]: /^\d{2}\d{2}\d{2}-\d{4}$/, // eg. DDMMYY-XXXX
  [CountryCodeEnum.LI]: /^\d{4,12}$/, // eg. from 4 to 12 digits, because 0 are omitted
  [CountryCodeEnum.NO]: /^\d{11}$/, // eg. 11 digits
  [CountryCodeEnum.TR]: /^\d{10}$/, // eg. 10 digits
  [CountryCodeEnum.DE]:
    /^(^[1-9][0-9]{9,11}$)|(^[0-9]{2,3}\/[0-9]{3,4}\/[0-9]{4,5}(\/[0-9]{1,2})?$)/, // eg. 9-11 digits or '2-3 digits'/'3-4 digits'/'4-5 digits/1-2 digits'
  [CountryCodeEnum.RO]:
    /^[1-9][0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[0-9]{6}$/,
  [CountryCodeEnum.LT]: /^[3-6]\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{4}$/,
  [CountryCodeEnum.IT]:
    /^([A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST]{1}[0-9LMNPQRSTUV]{2}[A-Z]{1}[0-9LMNPQRSTUV]{3}[A-Z]{1})$|([0-9]{11})$/,
  [CountryCodeEnum.CY]: /^\d{8}[A-Z]$/,
  [CountryCodeEnum.PL]: /^\d{10}$/,
};

export function isTaxIdValid(
  value: string,
  countryCode?: CountryCodeEnum
): boolean {
  if (countryCode == null) {
    return true;
  }

  const trimmedValue = value.trim();

  // If the country is not supported by the validator library, we need to validate the tax ID manually
  if (unsupportedCountries.includes(countryCode)) {
    const countryTaxIdRegex = taxIdRegex[countryCode];

    return countryTaxIdRegex.test(trimmedValue);
  }

  const validatorLocale = getValidatorLocaleFromCountryCode(countryCode);

  try {
    const isTaxIdValidLibrary = isTaxID(trimmedValue, validatorLocale);

    // Needed because we had a problem with a user from Italy where the library failed on his side
    // but not on our tests
    if (!isTaxIdValidLibrary && countriesToDoubleCheck.includes(countryCode)) {
      const countryTaxIdRegex = taxIdRegex[countryCode];

      return countryTaxIdRegex.test(trimmedValue);
    }

    return isTaxIdValidLibrary;
  } catch {
    // If error is thrown, the validator library does not support the selected country
    // TODO: Handle unsupported countries once we discover them
    return true;
  }
}
